import {
  Pressable,
  StyleSheet,
  Text,
  type PressableProps,
  type StyleProp,
  type ViewStyle,
} from "react-native";

import type { ReactNode } from "react";

import { Icon, type IconName } from "../Icon/Icon";
import { webTapFloor, type UiTheme } from "../theme";

// THE shared icon-only action button / toggle — one 36px box, one hitSlop, one
// active treatment, so the app stops hand-rolling a `headerIconButton` /
// `iconBtn` StyleSheet per surface (next/consolidation.md deferred `<IconButton>`).
// Compact box 36 + hitSlop 8 → ≥44 touch on native (density bounded by
// legibility, never an inflated visual box). react-native-web's Pressable
// doesn't implement hitSlop at all (DS-48): `box.minWidth`/`minHeight` carry
// `webTapFloor` below as a web-only floor to 44, no-op on native.
// `active` is a toggle state: border + brand tint AND
// accessibilityState.selected — never colour alone.
//
// `variant` (added with the TimerBanner lift, Fase 2 — its real callsites being
// the timer control rows): `default` is the neutral bordered box above (the
// logger 🕐/ⓘ toggles); `primary` is a brand-filled action for buttons that
// sit ON a coloured banner (the rest/hold timer controls, white glyph on
// brand600 so they read against the brand500 banner); `cancel` is the muted
// surface2 outline for the destructive ✕ (paired with a `close` icon + spoken
// label, so it is never colour-only); `destructive` keeps the neutral box but
// paints the glyph `danger` — the icon twin of RowAction's destructive label
// (the Library card's trash, HR-7). Never colour-only: it pairs with an
// unambiguous glyph + spoken label. `disabled` dims + blocks the press and
// sets accessibilityState.disabled.
//
// `locked` (added AP-0g, closes CN-superset-iconbox) is "disabled-looking but
// still tappable": it dims like `disabled` but keeps the press LIVE so the
// caller can answer with a "why it's locked" balloon (the logger's ☰ superset
// menu while editing a past session). It does NOT set accessibilityState.disabled
// — announcing a live, tappable control as "disabled" is a name/role/value
// contradiction (SC 4.1.2, a11y audit AP-0g); the caller pairs `locked` with an
// accessibilityHint that explains the lock instead. Use `disabled` for
// fully-inert; `locked` when a tap must still explain itself — this is why
// SupersetTabs no longer hand-rolls a 36-box.
//
// `label` (MX-5 slice 3) is the one alternative to a glyph: a SHORT numeric
// caption inside the same box — the hold timer's `−10` / `+10`, which the design
// writes as the number, not as a bare ∓ (next/effort-universal.md §design table,
// mockup state 2). A minus glyph alone cannot say by how much, and the step is
// 10 here but 30 on the rest timer, so the amount has to be visible and not only
// spoken. Same box, same slop, same tones — it is a caption swap, not a second
// button primitive. Exclusive with `name` by type: a box carries a glyph or a
// number, never both (there is no room for both at 36pt, and two signals for one
// action read as two actions).
export type IconButtonVariant = "default" | "primary" | "cancel" | "destructive";

type IconButtonBase = {
  theme: UiTheme;
  onPress: () => void;
  accessibilityLabel: string;
  /** Visual tone. `default` neutral box, `primary` brand fill (on a coloured
   *  banner), `cancel` muted outline. Defaults to `default`. */
  variant?: IconButtonVariant;
  /** Toggle state — brand-tinted border + fill + accessibilityState.selected.
   *  Only meaningful for the `default` variant. */
  active?: boolean;
  /** Disclosure state. When set, the button IS a disclosure (reveals content
   *  below), so it announces `accessibilityState.expanded` instead of
   *  `selected` — the correct AT semantic for the note/instructions toggles and
   *  the codebase's `useDisclosure` convention. Pair with `active` for the
   *  visual tint; leave undefined for a plain toggle (then `active`→selected). */
  expanded?: boolean;
  /** Opt-in active glyph treatment (DS-14). `fill-pr` paints the glyph SOLID in
   *  the `pr` status gold when `active` — the favourite ★ pinned as "current
   *  block", styled like a PR trophy. Outline→solid fill is a shape change, so
   *  the gold is reinforcement, not the sole cue. Only this callsite opts in;
   *  every other `active` toggle keeps the neutral ink2 outline. */
  activeGlyph?: "fill-pr";
  /** Dim + block the press; sets accessibilityState.disabled. */
  disabled?: boolean;
  /** Dim but keep the press LIVE (does NOT announce disabled) so the caller can
   *  surface a "why it's locked" tip — pair it with accessibilityHint. See the
   *  type doc above. */
  locked?: boolean;
  /** Fill the parent slot instead of the fixed 36 box (min 44 tall). For a
   *  button that OWNS a laid-out slot rather than sitting in a row of peers —
   *  MX-5's start chip takes the meter's whole 76×44 column, so it has no
   *  neighbours and needs no hitSlop arithmetic at all. */
  stretch?: boolean;
  /** Touch expansion. Defaults to 8 (→ ≥44). Pass `{top,bottom}` in tight
   *  horizontal rows so adjacent buttons' zones don't overlap. */
  hitSlop?: PressableProps["hitSlop"];
  /** Layout-only override (margins/flex). Merged last — e.g. the timer
   *  banner's extra leading gap before the destructive ✕. */
  style?: StyleProp<ViewStyle>;
  /** Extra spoken context (unchanged tap; no visual effect). */
  accessibilityHint?: string;
  /**
   * Wraps the GLYPH ONLY — the seam <UnseenCue> uses to teach an icon-only
   * control that it exists (EQ-1-GYM-COLDSTART R7/R8).
   *
   * ⛔ Deliberately wraps the glyph and not the box. Anything that scaled or
   * decorated the 36pt box would change what the eye reads as the tap target
   * and, in a tight row, could put two touch zones on the same pixels — the
   * exact clearance the ✕ was tuned for. Layout, hitSlop and the spoken name
   * are untouched; only the drawing inside the box changes.
   *
   * A render prop rather than a component prop so the caller composes it with
   * the theme and state it already has, and so this primitive stays ignorant
   * of what is being announced.
   */
  renderGlyph?: (glyph: ReactNode) => ReactNode;
};

export type IconButtonProps = IconButtonBase &
  (
    | { name: IconName; label?: never }
    /** Short numeric caption instead of the glyph — see the note above. Keep it
     *  to ~3 characters: the box stays 36pt so a word would clip. */
    | { name?: never; label: string }
  );

export function IconButton({
  theme,
  name,
  label,
  onPress,
  accessibilityLabel,
  variant = "default",
  active: activeProp,
  activeGlyph,
  expanded,
  disabled = false,
  locked = false,
  stretch = false,
  hitSlop = 8,
  style,
  accessibilityHint,
  renderGlyph,
}: IconButtonProps) {
  const styles = makeStyles(theme);
  // Defaulted HERE, not in the destructuring: whether the caller PASSED
  // `active` is what decides the ARIA attribute below, and a destructuring
  // default erases that distinction.
  const active = activeProp ?? false;
  // Dimmed either way (visual "not the usual" cue); only true `disabled` blocks
  // the press AND announces disabled. `locked` stays an enabled button for the
  // a11y tree — its lock is spoken via the caller's accessibilityHint.
  const dimmed = disabled || locked;
  // DS-14: the pinned favourite ★ paints SOLID in the pr gold (glyph + fill),
  // the same PR treatment as the trophy in the session summary. Shape (outline→
  // solid) carries the state too, so gold is never the only cue.
  const goldGlyph = active && activeGlyph === "fill-pr";
  const glyphColor = goldGlyph
    ? theme.palette.pr
    : variant === "primary"
      ? theme.palette.inkOnBrand
      : variant === "destructive"
        ? theme.palette.danger
        : theme.palette.ink2;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      // A disclosure announces `expanded`; a plain toggle announces `selected`.
      // Never both — that's a contradictory name/role/value (SC 4.1.2).
      accessibilityState={
        expanded === undefined
          ? { selected: active, disabled }
          : { expanded, disabled }
      }
      // ⚠️ RNW does NOT translate `accessibilityState` into `aria-*` — Chip.tsx
      // measured this headless for `expanded` and the same hole swallows
      // `selected`, so on web this box's toggle state was spoken to nobody. It
      // became load-bearing with EQ-1-PIN-DEFAULT's ★, whose whole meaning IS
      // its state. RN maps `aria-*` natively too, so this is one prop for both
      // hosts, exactly as Chip does it.
      //
      // ⛔ Only when the caller PASSED `active`, never on its default. Most
      // IconButtons are plain actions (a ✕, a dismiss), and `aria-selected` is
      // not a valid attribute on `role="button"` — emitting it everywhere would
      // reshape the ARIA contract of every callsite in the app to serve one.
      // Same asymmetry Chip.tsx uses for `expanded`, and for the same reason.
      {...(expanded !== undefined
        ? { "aria-expanded": expanded }
        : activeProp !== undefined
          ? { "aria-selected": activeProp }
          : {})}
      disabled={disabled}
      onPress={onPress}
      // A stretched button already fills a ≥44 slot — slop on top of that would
      // reach into whatever sits next to the slot.
      hitSlop={stretch ? undefined : hitSlop}
      style={({ pressed }) => [
        styles.box,
        stretch && styles.stretch,
        variant === "primary" && styles.primary,
        variant === "cancel" && styles.cancel,
        active && styles.active,
        pressed && (variant === "default" ? styles.pressed : styles.pressedFill),
        dimmed && styles.disabled,
        // Merged LAST, as the prop documents. It was declared but never
        // destructured, so every caller's layout override was silently dropped
        // — including TimerBanner's leading gap before the destructive ✕, the
        // one that exists to stop a ✓/✕ mis-tap (inbox 9b5daf1a). Found by the
        // consolidation review 2026-07-22; predates MX-5.
        style,
      ]}
    >
      {label !== undefined ? (
        // Mono + tabular so `−10` and `+10` are the same width and the row
        // doesn't twitch when the direction flips; sm, not md, because the sign
        // and two digits have to clear the 36pt box's 1pt border on both sides.
        <Text style={[styles.label, { color: glyphColor }]} numberOfLines={1}>
          {label}
        </Text>
      ) : name ? (
        (() => {
          const glyph = (
            <Icon
              name={name}
              size={theme.typography.sizes.md}
              color={glyphColor}
              fill={goldGlyph ? theme.palette.pr : "none"}
            />
          );
          // Wrapped only when a caller asked: an extra view on every icon
          // button in the app would be a layout node nobody wanted. The
          // Pressable above owns the name and the role either way.
          return renderGlyph ? renderGlyph(glyph) : glyph;
        })()
      ) : null}
    </Pressable>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // Numeric caption (see `label`): mono + tabular-nums, the same treatment
    // every number in the meter gets, so the step amount reads as a quantity
    // rather than as a word. Colour rides the glyph colour, so the variant
    // tones stay in one place.
    label: {
      fontFamily: ty.fonts.mono,
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    // DS-9 (Antenore 2026-07-04, variante C del mockup): surface2 fill +
    // borderStrong — two weak boundary cues that compound, so the tap box is
    // findable on every theme family (border alone was 1.3–1.9:1 vs
    // surface). borderStrong itself was retuned to ≥3:1 in the brand and
    // rosepine families alongside this.
    box: {
      width: 36,
      height: 36,
      minWidth: webTapFloor,
      minHeight: webTapFloor,
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.borderStrong,
      backgroundColor: p.surface2,
      alignItems: "center",
      justifyContent: "center",
    },
    // Overrides the fixed box: the parent slot dictates the size (MeterRow's
    // column already guarantees ≥44 tall), so no hitSlop is needed.
    stretch: { width: "100%", height: "100%", minHeight: 44 },
    primary: { backgroundColor: p.brand600, borderColor: p.brand600 },
    // Deliberately quieter than the new default (plain border): the muted ✕
    // that sits next to a primary in the timer banners.
    cancel: { backgroundColor: p.surface2, borderColor: p.border },
    active: { borderColor: p.ink2, backgroundColor: p.brand100 },
    pressed: { backgroundColor: p.surface },
    pressedFill: { opacity: 0.7 },
    disabled: { opacity: 0.4 },
  });
}
